"use client"

/**
 * Category picker — same search + tree chrome as Locations, using the
 * centralized exam category library.
 */

import * as React from "react"

import { ExamCategoryTree } from "@/components/exam-categories/exam-category-tree"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group"
import { cn } from "@/lib/utils"
import {
  getSearchExpandedIds,
  type ExamCategory,
  type ExamCategoryTreeNode,
} from "@/lib/exam-categories"
import {
  expandedIdsForCategorySelection,
  useScrollCategoryPickerToSelection,
} from "@/lib/category-picker-scroll"

export function ExamCategorySearchInput({
  value,
  onChange,
  inputRef,
}: {
  value: string
  onChange: (next: string) => void
  inputRef?: React.RefObject<HTMLInputElement | null>
}) {
  return (
    <InputGroup>
      <InputGroupAddon align="inline-start">
        <i className="fa-light fa-magnifying-glass text-xs" aria-hidden="true" />
      </InputGroupAddon>
      <InputGroupInput
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search categories"
        aria-label="Search categories"
      />
    </InputGroup>
  )
}

export interface ExamCategoryPickerPanelProps {
  categories: ExamCategory[]
  tree: ExamCategoryTreeNode[]
  value: string[]
  onChange: (categoryIds: string[]) => void
  multiple?: boolean
  className?: string
}

export function ExamCategoryPickerPanel({
  categories,
  tree,
  value,
  onChange,
  multiple = true,
  className,
}: ExamCategoryPickerPanelProps) {
  const [query, setQuery] = React.useState("")
  const [expandedIds, setExpandedIds] = React.useState<Record<string, boolean>>(
    () => expandedIdsForCategorySelection(categories, value),
  )
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const scrollerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  const valueKey = value.join("|")

  React.useEffect(() => {
    if (value.length === 0) return
    setExpandedIds((current) =>
      expandedIdsForCategorySelection(categories, value, current),
    )
  }, [categories, valueKey, value.length])

  const mergedExpanded = React.useMemo(() => {
    const searchExpanded = getSearchExpandedIds(categories, query)
    if (searchExpanded.size === 0) return expandedIds
    const next = { ...expandedIds }
    for (const id of searchExpanded) next[id] = true
    return next
  }, [categories, expandedIds, query])

  const expandedLayoutKey = React.useMemo(
    () =>
      Object.entries(mergedExpanded)
        .filter(([, open]) => open)
        .map(([id]) => id)
        .sort()
        .join("|"),
    [mergedExpanded],
  )

  useScrollCategoryPickerToSelection(scrollerRef, {
    categoryIds: value,
    searchQuery: query,
    layoutKey: expandedLayoutKey,
  })

  const toggleOrPick = React.useCallback(
    (categoryId: string) => {
      if (!multiple) {
        onChange([categoryId])
        return
      }
      const selected = new Set(value)
      if (selected.has(categoryId)) selected.delete(categoryId)
      else selected.add(categoryId)
      onChange([...selected])
    },
    [multiple, onChange, value],
  )

  return (
    <div className={cn("flex h-full max-h-[inherit] min-h-0 flex-col", className)}>
      <div className="shrink-0 border-b border-border p-2">
        <ExamCategorySearchInput
          inputRef={searchInputRef}
          value={query}
          onChange={setQuery}
        />
      </div>
      <div
        ref={scrollerRef}
        data-slot="category-picker-panel"
        className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-2"
        role="listbox"
        aria-label="Categories"
        aria-multiselectable={multiple || undefined}
      >
        <ExamCategoryTree
          tree={tree}
          categories={categories}
          selectedIds={value}
          showCheckbox={multiple}
          expandedIds={mergedExpanded}
          searchQuery={query}
          onToggleExpand={(id, open) =>
            setExpandedIds((current) => ({ ...current, [id]: open }))
          }
          onSelect={toggleOrPick}
        />
      </div>
    </div>
  )
}
